import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Switch from '@mui/material/Switch';
import { DocumentType } from 'common/models/Document';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import React, { useCallback, useEffect, useState } from 'react';
import { CreateDocumentType } from 'services/DocumentsService';

interface Props {
  open: boolean;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string, newRowData: DocumentType) => void;
}

export default function CreateDocumentTypeDialog({
  open,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [name, setName] = useState('');
  const [isClients, setIsClients] = useState(false);
  const [isContacts, setIsContacts] = useState(false);
  const [isCandidates, setIsCandidates] = useState(false);
  const [isJobs, setIsJobs] = useState(false);
  const [isPlacements, setIsPlacements] = useState(false);
  const [isOpportunities, setIsOpportunities] = useState(false);
  const [isSites, setIsSites] = useState(false);
  const [defaultDocumentType, setDefaultDocumentType] = useState(false);
  const [defaultFormattedCVType, setDefaultFormattedCVType] = useState(false);
  const [defaultSubmittedCVType, setDefaultSubmittedCVType] = useState(false);
  const [defaultReferenceCheckType, setDefaultReferenceCheckType] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setIsClients(false);
      setIsContacts(false);
      setIsCandidates(false);
      setIsJobs(false);
      setIsPlacements(false);
      setIsOpportunities(false);
      setIsSites(false);
      setDefaultDocumentType(false);
      setDefaultFormattedCVType(false);
      setDefaultSubmittedCVType(false);
      setDefaultReferenceCheckType(false);
    }
  }, [open]);

  const saveChangesHandler = useCallback(async () => {
    loadingHandler?.(true);
    const d: DocumentType = {
      id: 0,
      name: name,
      isClientsEnabled: isClients,
      isContactsEnabled: isContacts,
      isCandidatesEnabled: isCandidates,
      isJobsEnabled: isJobs,
      isPlacementsEnabled: isPlacements,
      isOpportunitiesEnabled: isOpportunities,
      isSitesEnabled: isSites,
      defaultDocumentType: defaultDocumentType,
      defaultFormattedCVType: defaultFormattedCVType,
      defaultSubmittedCVType: defaultSubmittedCVType,
      defaultReferenceCheckType: defaultReferenceCheckType,
    };
    const res = await CreateDocumentType(d, errorHandler);
    if (res) {
      successHandler?.('Type Created', res);
      closeHandler();
    }
    loadingHandler?.(false);
  }, [
    name,
    isClients,
    isContacts,
    isCandidates,
    isJobs,
    isPlacements,
    isOpportunities,
    isSites,
    defaultDocumentType,
    defaultFormattedCVType,
    defaultSubmittedCVType,
    defaultReferenceCheckType,
    closeHandler,
    loadingHandler,
    errorHandler,
    successHandler,
  ]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Create Document Type</DialogTitle>
      <DialogContent>
        <RWTextFieldComponent
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sxOptions={{ mt: 1, mb: 2 }}
        />
        <Box display="flex">
          <FormControl sx={{ flex: '1 1 0' }}>
            <FormLabel>Module Visibility</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={isCandidates}
                  onChange={(e) => setIsCandidates(e.target.checked)}
                />
              }
              label="Candidates"
            />
            <FormControlLabel
              control={
                <Switch checked={isClients} onChange={(e) => setIsClients(e.target.checked)} />
              }
              label="Clients"
            />
            <FormControlLabel
              control={
                <Switch checked={isContacts} onChange={(e) => setIsContacts(e.target.checked)} />
              }
              label="Contacts"
            />
            <FormControlLabel
              control={<Switch checked={isJobs} onChange={(e) => setIsJobs(e.target.checked)} />}
              label="Jobs"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isOpportunities}
                  onChange={(e) => setIsOpportunities(e.target.checked)}
                />
              }
              label="Opportunities"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isPlacements}
                  onChange={(e) => setIsPlacements(e.target.checked)}
                />
              }
              label="Placements"
            />
            <FormControlLabel
              control={<Switch checked={isSites} onChange={(e) => setIsSites(e.target.checked)} />}
              label="Sites"
            />
          </FormControl>
          <FormControl sx={{ flex: '1 1 0' }}>
            <FormLabel>Default Types</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={defaultDocumentType}
                  onChange={(e) => setDefaultDocumentType(e.target.checked)}
                />
              }
              label="Document Type"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={defaultFormattedCVType}
                  onChange={(e) => setDefaultFormattedCVType(e.target.checked)}
                />
              }
              label="Formatted CV Type"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={defaultSubmittedCVType}
                  onChange={(e) => setDefaultSubmittedCVType(e.target.checked)}
                />
              }
              label="Submitted CV Type"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={defaultReferenceCheckType}
                  onChange={(e) => setDefaultReferenceCheckType(e.target.checked)}
                />
              }
              label="Reference Check Type"
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button variant="contained" color="success" disabled={!name} onClick={saveChangesHandler}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
